import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import ResourceCard from './components/ResourceCard';
import DownloadCenter from './components/DownloadCenter';

const StudyMaterials = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedKnowledgeAreas, setSelectedKnowledgeAreas] = useState([]);
  const [contentTypeFilter, setContentTypeFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [completionFilter, setCompletionFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const certificationLevels = [
    { id: 'all', label: 'All Resources', icon: 'Library' },
    { id: 'ccba', label: 'CCBA', icon: 'BookOpen' },
    { id: 'cbap', label: 'CBAP', icon: 'GraduationCap' },
    { id: 'cbda', label: 'CBDA', icon: 'Database' }
  ];

  const knowledgeAreas = [
    'Business Analysis Planning and Monitoring',
    'Elicitation and Collaboration',
    'Requirements Life Cycle Management',
    'Strategy Analysis',
    'Requirements Analysis and Design Definition',
    'Solution Evaluation'
  ];

  const contentTypes = [
    { value: '', label: 'All Content Types' },
    { value: 'reading-guide', label: 'Reading Guides' },
    { value: 'knowledge-area', label: 'Knowledge Area Resources' },
    { value: 'article', label: 'Articles' },
    { value: 'downloadable', label: 'Downloadable Materials' },
    { value: 'interactive', label: 'Interactive Guides' },
    { value: 'checklist', label: 'Checklists' }
  ];

  const difficultyLevels = [
    { value: '', label: 'All Difficulty Levels' },
    { value: 'beginner', label: 'Beginner' },
    { value: 'intermediate', label: 'Intermediate' },
    { value: 'advanced', label: 'Advanced' }
  ];

  const completionStatuses = [
    { value: '', label: 'All Status' },
    { value: 'not-started', label: 'Not Started' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' }
  ];

  const studyResources = [
    {
      id: 1,
      title: 'BABOK Guide v3 - Chapter 1: Introduction to Business Analysis',
      type: 'reading-guide',
      knowledgeArea: 'Business Analysis Planning and Monitoring',
      difficulty: 'beginner',
      readingTime: 25,
      rating: 4.8,
      ratingCount: 342,
      certificationLevel: 'ccba',
      description: 'Comprehensive overview of business analysis fundamentals, key concepts, and the role of business analysts in organizations.',
      completionStatus: 'completed',
      bookmarked: true,
      downloadable: true,
      format: 'PDF',
      fileSize: '2.4 MB',
      alt: 'BABOK Guide Chapter 1 cover showing introduction to business analysis fundamentals'
    },
    {
      id: 2,
      title: 'Stakeholder Analysis Framework - Visual Guide',
      type: 'knowledge-area',
      knowledgeArea: 'Business Analysis Planning and Monitoring',
      difficulty: 'intermediate',
      readingTime: 15,
      rating: 4.9,
      ratingCount: 287,
      certificationLevel: 'cbap',
      description: 'Interactive visual framework for conducting stakeholder analysis with practical templates and real-world examples.',
      completionStatus: 'in-progress',
      bookmarked: true,
      downloadable: true,
      format: 'Interactive PDF',
      fileSize: '5.1 MB',
      alt: 'Stakeholder analysis framework diagram with power-interest grid and engagement strategies'
    },
    {
      id: 3,
      title: 'Elicitation Techniques Masterclass',
      type: 'article',
      knowledgeArea: 'Elicitation and Collaboration',
      difficulty: 'advanced',
      readingTime: 35,
      rating: 4.7,
      ratingCount: 198,
      certificationLevel: 'cbap',
      description: 'In-depth exploration of advanced elicitation techniques including workshops, interviews, observation, and prototyping with case studies.',
      completionStatus: 'not-started',
      bookmarked: false,
      downloadable: false,
      format: 'Online Article',
      fileSize: null,
      alt: 'Elicitation techniques comparison chart showing workshops, interviews, and observation methods'
    },
    {
      id: 4,
      title: 'Requirements Traceability Matrix Template',
      type: 'downloadable',
      knowledgeArea: 'Requirements Life Cycle Management',
      difficulty: 'intermediate',
      readingTime: 10,
      rating: 4.6,
      ratingCount: 421,
      certificationLevel: 'ccba',
      description: 'Ready-to-use traceability matrix template with instructions for tracking requirements throughout the project lifecycle.',
      completionStatus: 'completed',
      bookmarked: true,
      downloadable: true,
      format: 'Excel Template',
      fileSize: '1.2 MB',
      alt: 'Requirements traceability matrix template showing requirement IDs, sources, and test cases'
    },
    {
      id: 5,
      title: 'Strategy Analysis: Business Model Canvas Guide',
      type: 'interactive',
      knowledgeArea: 'Strategy Analysis',
      difficulty: 'intermediate',
      readingTime: 30,
      rating: 4.9,
      ratingCount: 356,
      certificationLevel: 'cbap',
      description: 'Interactive guide to using Business Model Canvas for strategy analysis with fillable templates and industry examples.',
      completionStatus: 'in-progress',
      bookmarked: true,
      downloadable: true,
      format: 'Interactive Guide',
      fileSize: '8.7 MB',
      alt: 'Business Model Canvas template with nine building blocks for strategic planning'
    },
    {
      id: 6,
      title: 'Requirements Modeling Techniques Checklist',
      type: 'checklist',
      knowledgeArea: 'Requirements Analysis and Design Definition',
      difficulty: 'beginner',
      readingTime: 8,
      rating: 4.5,
      ratingCount: 512,
      certificationLevel: 'ccba',
      description: 'Quick reference checklist covering all major requirements modeling techniques including use cases, data models, and process flows.',
      completionStatus: 'not-started',
      bookmarked: false,
      downloadable: true,
      format: 'PDF Checklist',
      fileSize: '0.8 MB',
      alt: 'Requirements modeling checklist with use cases, data models, and process flow diagrams'
    },
    {
      id: 7,
      title: 'Solution Evaluation Metrics Framework',
      type: 'knowledge-area',
      knowledgeArea: 'Solution Evaluation',
      difficulty: 'advanced',
      readingTime: 40,
      rating: 4.8,
      ratingCount: 234,
      certificationLevel: 'cbap',
      description: 'Comprehensive framework for evaluating solution performance with KPIs, measurement techniques, and reporting templates.',
      completionStatus: 'not-started',
      bookmarked: false,
      downloadable: true,
      format: 'PDF Guide',
      fileSize: '6.3 MB',
      alt: 'Solution evaluation framework showing KPIs, metrics, and performance measurement dashboard'
    },
    {
      id: 8,
      title: 'BABOK Guide v3 - Chapter 3: Business Analysis Planning',
      type: 'reading-guide',
      knowledgeArea: 'Business Analysis Planning and Monitoring',
      difficulty: 'intermediate',
      readingTime: 45,
      rating: 4.7,
      ratingCount: 389,
      certificationLevel: 'cbap',
      description: 'Detailed chapter summary covering planning approaches, stakeholder engagement, governance, and information management.',
      completionStatus: 'in-progress',
      bookmarked: true,
      downloadable: true,
      format: 'PDF',
      fileSize: '3.8 MB',
      alt: 'BABOK Chapter 3 cover page with business analysis planning knowledge area overview'
    },
    {
      id: 9,
      title: 'Data Analytics for Business Analysts - CBDA Focus',
      type: 'article',
      knowledgeArea: 'Requirements Analysis and Design Definition',
      difficulty: 'advanced',
      readingTime: 50,
      rating: 4.9,
      ratingCount: 167,
      certificationLevel: 'cbda',
      description: 'Specialized guide for CBDA certification covering data analytics techniques, visualization, and data-driven decision making.',
      completionStatus: 'not-started',
      bookmarked: false,
      downloadable: false,
      format: 'Online Article',
      fileSize: null,
      alt: 'Data analytics dashboard showing charts, graphs, and business intelligence visualizations'
    },
    {
      id: 10,
      title: 'Collaboration Tools and Techniques Reference',
      type: 'downloadable',
      knowledgeArea: 'Elicitation and Collaboration',
      difficulty: 'beginner',
      readingTime: 20,
      rating: 4.6,
      ratingCount: 445,
      certificationLevel: 'ccba',
      description: 'Quick reference guide to collaboration tools including virtual whiteboards, survey tools, and facilitation techniques.',
      completionStatus: 'completed',
      bookmarked: true,
      downloadable: true,
      format: 'PDF Reference',
      fileSize: '2.1 MB',
      alt: 'Collaboration tools reference sheet showing virtual whiteboard, survey, and facilitation icons'
    },
    {
      id: 11,
      title: 'Requirements Life Cycle Management Best Practices',
      type: 'knowledge-area',
      knowledgeArea: 'Requirements Life Cycle Management',
      difficulty: 'intermediate',
      readingTime: 35,
      rating: 4.8,
      ratingCount: 298,
      certificationLevel: 'cbap',
      description: 'Industry best practices for managing requirements from inception through implementation with change control processes.',
      completionStatus: 'not-started',
      bookmarked: false,
      downloadable: true,
      format: 'PDF Guide',
      fileSize: '4.5 MB',
      alt: 'Requirements lifecycle diagram showing stages from elicitation to implementation and maintenance'
    },
    {
      id: 12,
      title: 'SWOT Analysis Template and Guide',
      type: 'interactive',
      knowledgeArea: 'Strategy Analysis',
      difficulty: 'beginner',
      readingTime: 15,
      rating: 4.7,
      ratingCount: 523,
      certificationLevel: 'ccba',
      description: 'Interactive SWOT analysis template with step-by-step instructions and examples from various industries.',
      completionStatus: 'completed',
      bookmarked: true,
      downloadable: true,
      format: 'Interactive Template',
      fileSize: '3.2 MB',
      alt: 'SWOT analysis matrix template with strengths, weaknesses, opportunities, and threats quadrants'
    }
  ];

  const filteredResources = studyResources?.filter(resource => {
    const matchesTab = activeTab === 'all' || resource?.certificationLevel === activeTab;
    const matchesSearch = !searchTerm || 
      resource?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      resource?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesKnowledgeArea = selectedKnowledgeAreas?.length === 0 || 
      selectedKnowledgeAreas?.includes(resource?.knowledgeArea);
    const matchesContentType = !contentTypeFilter || resource?.type === contentTypeFilter;
    const matchesDifficulty = !difficultyFilter || resource?.difficulty === difficultyFilter;
    const matchesCompletion = !completionFilter || resource?.completionStatus === completionFilter;

    return matchesTab && matchesSearch && matchesKnowledgeArea && 
           matchesContentType && matchesDifficulty && matchesCompletion;
  });

  const handleKnowledgeAreaChange = (area) => {
    setSelectedKnowledgeAreas(prev => 
      prev?.includes(area) 
        ? prev?.filter(a => a !== area)
        : [...prev, area]
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedKnowledgeAreas([]);
    setContentTypeFilter('');
    setDifficultyFilter('');
    setCompletionFilter('');
  };

  const hasActiveFilters = searchTerm || selectedKnowledgeAreas?.length > 0 || 
    contentTypeFilter || difficultyFilter || completionFilter;

  return (
    <>
      <Helmet>
        <title>Study Materials - BABOK Master Pro</title>
        <meta name="description" content="Access comprehensive BABOK study materials including reading guides, knowledge area resources, articles, and downloadable content organized by certification level" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-[60px]">
          <div className="max-w-[1536px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
            {/* Page Header */}
            <div className="mb-6 md:mb-8">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">
                    Study Materials Library
                  </h1>
                  <p className="text-sm md:text-base text-muted-foreground">
                    Comprehensive BABOK learning resources organized by certification level
                  </p>
                </div>
                <Button
                  variant="outline"
                  iconName="Download"
                  onClick={() => document?.getElementById('download-center')?.scrollIntoView({ behavior: 'smooth' })}
                  className="hidden md:inline-flex"
                >
                  Download Center
                </Button>
              </div>

              {/* Certification Level Tabs */}
              <div className="flex flex-wrap gap-2 mb-6">
                {certificationLevels?.map(level => (
                  <button
                    key={level?.id}
                    onClick={() => setActiveTab(level?.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                      activeTab === level?.id
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-card text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={level?.icon} size={18} />
                    <span>{level?.label}</span>
                  </button>
                ))}
              </div>

              {/* Search and Filter Bar */}
              <div className="bg-card rounded-lg p-4 shadow-elevation-1">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1">
                    <Input
                      placeholder="Search study materials..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e?.target?.value)}
                      className="w-full"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant={showFilters ? 'default' : 'outline'}
                      iconName="Filter"
                      onClick={() => setShowFilters(!showFilters)}
                    >
                      Filters
                    </Button>
                    {hasActiveFilters && (
                      <Button
                        variant="ghost"
                        iconName="X"
                        onClick={clearFilters}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {/* Filter Panel */}
                {showFilters && (
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <Select
                        label="Content Type"
                        options={contentTypes}
                        value={contentTypeFilter}
                        onChange={setContentTypeFilter}
                        placeholder="All Types"
                      />
                      <Select
                        label="Difficulty Level"
                        options={difficultyLevels}
                        value={difficultyFilter}
                        onChange={setDifficultyFilter}
                        placeholder="All Levels"
                      />
                      <Select
                        label="Completion Status"
                        options={completionStatuses}
                        value={completionFilter}
                        onChange={setCompletionFilter}
                        placeholder="All Status"
                      />
                      <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">
                          Knowledge Areas
                        </label>
                        <div className="max-h-40 overflow-y-auto space-y-2 bg-background p-3 rounded-md border border-input">
                          {knowledgeAreas?.map(area => (
                            <Checkbox
                              key={area}
                              label={area}
                              checked={selectedKnowledgeAreas?.includes(area)}
                              onChange={() => handleKnowledgeAreaChange(area)}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Resource Grid */}
            <div className="mb-12">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-foreground">
                  {filteredResources?.length} Resources Found
                </h2>
              </div>

              {filteredResources?.length === 0 ? (
                <div className="bg-card rounded-lg p-12 text-center shadow-elevation-1">
                  <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    No resources found
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Try adjusting your filters or search terms
                  </p>
                  <Button variant="outline" onClick={clearFilters}>
                    Clear All Filters
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredResources?.map(resource => (
                    <ResourceCard key={resource?.id} resource={resource} />
                  ))}
                </div>
              )}
            </div>

            {/* Download Center */}
            <div id="download-center">
              <DownloadCenter resources={studyResources?.filter(r => r?.downloadable)} />
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default StudyMaterials;